import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { reactLocalStorage } from "reactjs-localstorage";
import { BEAM_URL } from "../../services/api-url";

export const EMAIL_FLAGS_UPDATED = "email-flags-updated";

export function authHeaders() {
    const token = reactLocalStorage.get("token") || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export function highlightParam(flags = []) {
    const fields = new Set();
    flags.forEach((flag) => {
        if (flag.code === "admin_not_compliance") fields.add("adminEmail");
        if (flag.code === "admin_not_owner") {
            fields.add("adminEmail");
            fields.add("ownerEmail");
        }
    });
    return [...fields].join(",") || "adminEmail";
}

export function notifyEmailFlagsUpdated() {
    window.dispatchEvent(new Event(EMAIL_FLAGS_UPDATED));
}

export function NotificationBell() {
    const navigate = useNavigate();
    const panelRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [total, setTotal] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resolvingId, setResolvingId] = useState("");

    const loadCount = useCallback(async () => {
        if (!authHeaders().Authorization) {
            setTotal(0);
            return;
        }
        try {
            const res = await axios.get(`${BEAM_URL}/notifications/email-flags/count`, {
                headers: authHeaders(),
            });
            setTotal(Number(res?.data?.total) || 0);
        } catch (error) {
            setTotal(0);
        }
    }, []);

    const loadTop = useCallback(async () => {
        if (!authHeaders().Authorization) {
            setItems([]);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${BEAM_URL}/notifications/email-flags`, {
                params: { page: 1, limit: 10 },
                headers: authHeaders(),
            });
            const payload = res?.data || {};
            setItems(Array.isArray(payload.data) ? payload.data : []);
            if (Number.isFinite(Number(payload.total))) {
                setTotal(Number(payload.total));
            }
        } catch (error) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCount();
        const timer = setInterval(loadCount, 60000);
        window.addEventListener(EMAIL_FLAGS_UPDATED, loadCount);
        return () => {
            clearInterval(timer);
            window.removeEventListener(EMAIL_FLAGS_UPDATED, loadCount);
        };
    }, [loadCount]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleItemClick = (item) => {
        setOpen(false);
        const highlight = highlightParam(item.flags);
        navigate(`/tickets?buildingId=${item.id}&highlight=${encodeURIComponent(highlight)}`);
    };

    const handleResolve = async (event, item) => {
        event.stopPropagation();
        if (!item?._id) return;
        setResolvingId(item._id);
        try {
            await axios.patch(
                `${BEAM_URL}/notifications/email-flags/${item._id}/resolve`,
                {},
                { headers: authHeaders() },
            );
            setItems((prev) => prev.filter((flag) => flag._id !== item._id));
            setTotal((prev) => Math.max(0, prev - 1));
            notifyEmailFlagsUpdated();
        } catch (error) {
            console.error("Failed to resolve flag:", error);
        } finally {
            setResolvingId("");
        }
    };

    const badge = total > 99 ? "99+" : String(total);

    return (
        <li className="cs-notify" ref={panelRef}>
            <button
                type="button"
                className="cs-notify-bell"
                aria-label="Open notifications"
                onClick={() => {
                    const next = !open;
                    setOpen(next);
                    if (next) loadTop();
                }}
            >
                <i className="fas fa-bell"></i>
                {total > 0 && <span className="cs-notify-badge">{badge}</span>}
            </button>
            {open && (
                <div className="cs-notify-panel">
                    <div className="cs-notify-head">
                        <strong>Notifications</strong>
                        <span>{total} unresolved</span>
                    </div>
                    <div className="cs-notify-body">
                        {loading && <div className="cs-notify-empty">Loading…</div>}
                        {!loading && items.length === 0 && (
                            <div className="cs-notify-empty">No unresolved notifications</div>
                        )}
                        {!loading &&
                            items.map((item) => (
                                <div key={item._id || item.id} className="cs-notify-item">
                                    <button
                                        type="button"
                                        className="cs-notify-item-main"
                                        onClick={() => handleItemClick(item)}
                                    >
                                        <div className="cs-notify-item-title">
                                            {item.name || `Building ${item.id}`}
                                        </div>
                                        <div className="cs-notify-item-meta">
                                            {item.address ? `${item.address} · ` : ""}ID {item.id}
                                        </div>
                                        <ul className="cs-notify-reasons">
                                            {(item.flags || []).slice(0, 2).map((flag) => (
                                                <li key={flag.code}>{flag.label}</li>
                                            ))}
                                        </ul>
                                    </button>
                                    <button
                                        type="button"
                                        className="cs-notify-resolve"
                                        disabled={resolvingId === item._id}
                                        onClick={(event) => handleResolve(event, item)}
                                    >
                                        {resolvingId === item._id ? "Saving…" : "Resolve"}
                                    </button>
                                </div>
                            ))}
                    </div>
                    <button
                        type="button"
                        className="cs-notify-footer"
                        onClick={() => {
                            setOpen(false);
                            navigate("/notifications");
                        }}
                    >
                        View all notifications
                    </button>
                </div>
            )}
        </li>
    );
}
